/* eslint-disable no-restricted-syntax */
import {
	forwardRef, useEffect, useImperativeHandle, useMemo, useState,
} from 'react';
import { DocumentNode, useLazyQuery } from '@apollo/client';
import { ICellEditorParams, ICellEditor } from 'ag-grid-community';
import AsyncSelect from 'react-select/async';
import {
	MultiValue, SingleValue, CSSObjectWithLabel,
} from 'react-select';
import { AsyncSelectComponents } from 'wb-spotify/app/routes/superGrid/cellEditors/utils';

interface IAsyncGeoCellEditorParams extends ICellEditorParams {
	graphqlQuery: DocumentNode;
	valueFieldName: string;
	isMulti?: boolean;
	additionalVariables?: Record<string, unknown>;
}

interface OptionType {
	value: string;
	label: string;
	__typename?: string;
}

function findDataItems(obj: unknown): unknown[] | undefined {
	if (!obj || typeof obj !== 'object' || obj === null) return undefined;

	const objWithItems = obj as { items?: unknown };
	if (Array.isArray(objWithItems.items)) return objWithItems.items;

	for (const key of Object.keys(obj)) {
		const found = findDataItems((obj as Record<string, unknown>)[key]);
		if (found) return found;
	}

	return undefined;
}

/**
 * Extract IDs from value - handles both string arrays and object arrays
 */
function extractIds(value: any): string[] {
	if (!value) return [];
	if (!Array.isArray(value)) return [];

	// If already objects with value property, extract IDs
	if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null && 'value' in value[0]) {
		return value.map((item: any) => item.value).filter(Boolean);
	}

	// If strings, return as is
	if (typeof value[0] === 'string') {
		return value.filter(Boolean);
	}

	return [];
}

/**
 * Check if value has labels
 */
function hasLabels(value: any[] | undefined): boolean {
	if (!value || value.length === 0) {
		return false;
	}
	const firstItem = value[0];

	return (
		typeof firstItem === 'object'
		&& firstItem !== null
		&& 'label' in firstItem
		&& firstItem.label !== firstItem.value
	);
}

const AsyncGeoCellSelectEditor = forwardRef<ICellEditor, IAsyncGeoCellEditorParams>((
	{
		valueFieldName,
		graphqlQuery,
		additionalVariables,
		isMulti = true,
		stopEditing,
		...params
	},
	ref,
) => {
	const [loadSelectOptions] = useLazyQuery(graphqlQuery);
	const [selectedOptions, setSelectedOptions] = useState<OptionType[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isPreloading, setIsPreloading] = useState(false);

	// Extract IDs from current value
	const selectedIds = useMemo(() => extractIds(params?.value), [params?.value]);

	// Check if value already has labels
	const valueHasLabels = useMemo(() => hasLabels(params?.value), [params?.value]);

	// Preload selected values when editor opens (if they don't have labels)
	useEffect(() => {
		// If value already has labels, use it directly
		if (valueHasLabels && params?.value) {
			setSelectedOptions([...params.value]);
			setIsPreloading(false);

			return;
		}

		// If no IDs, clear selected options
		if (selectedIds.length === 0) {
			setSelectedOptions([]);
			setIsPreloading(false);

			return;
		}

		// Check if countryCode is required but missing
		const filters = additionalVariables?.filters as {
			geoType?: Array<{ value: string[] }>;
			geoCountryCode?: Array<{ value: string[] }>
		} | undefined;

		const hasGeoType = filters?.geoType && filters.geoType.length > 0;
		const hasCountryCode = filters?.geoCountryCode && filters.geoCountryCode.length > 0;

		if (hasGeoType && !hasCountryCode) {
			setSelectedOptions([]);
			setIsPreloading(false);

			return;
		}

		// Fetch labels for selected IDs
		setIsPreloading(true);
		const geoIdFilter = selectedIds.map(id => `geoId = '${id}'`).join(' OR ');

		loadSelectOptions({
			variables: {
				query: `(${geoIdFilter})`,
				...additionalVariables,
				limit: selectedIds.length,
			},
		}).then(({ data }) => {
			const items = (findDataItems(data) ?? []) as OptionType[];
			// Ensure items are in the same order as selectedIds
			const orderedItems = selectedIds
				.map(id => items.find((item: OptionType) => item?.value === id))
				.filter((item): item is OptionType => item !== undefined);
			setSelectedOptions(orderedItems.length > 0 ? orderedItems : items);
			setIsPreloading(false);
		}).catch(() => {
			setSelectedOptions([]);
			setIsPreloading(false);
		});
	}, [selectedIds, valueHasLabels, params?.value, loadSelectOptions, additionalVariables]);

	// Implement ICellEditor interface
	useImperativeHandle(ref, () => ({
		getValue() {
			// Return the selected options with labels
			return selectedOptions;
		},
		isCancelAfterEnd(): boolean {
			return false;
		},
		isPopup(): boolean {
			return true;
		},
	}), [selectedOptions]);

	// Load options for search - AsyncSelect supports both Promise and callback
	const loadOptions = useMemo(() => {
		const loadOptionsInternal = async (inputValue: string): Promise<OptionType[]> => {
			// Check if countryCode is required but missing
			const filters = additionalVariables?.filters as {
				geoType?: Array<{ value: string[] }>;
				geoCountryCode?: Array<{ value: string[] }>
			} | undefined;

			const hasGeoType = filters?.geoType && filters.geoType.length > 0;
			const hasCountryCode = filters?.geoCountryCode && filters.geoCountryCode.length > 0;

			if (hasGeoType && !hasCountryCode) {
				// Return selected options only if available, otherwise empty
				return selectedOptions;
			}

			setIsLoading(true);

			try {
				const { data } = await loadSelectOptions({
					variables: {
						query: inputValue !== ''
							? `${valueFieldName} LIKE '%${inputValue}%'`
							: undefined,
						...additionalVariables,
					},
				});

				const items = findDataItems(data);
				const searchResults = (items ?? []) as OptionType[];

				// Debug: log if we got results
				if (searchResults.length === 0 && inputValue === '') {
					console.log('No options loaded on initial mount. Data:', data);
				}

				// Always put selected options at the top, then other results
				// Don't filter out selected items - keep them visible in the list
				if (selectedOptions.length > 0) {
					const selectedIdsSet = new Set(selectedIds);
					// Separate selected items that appear in search results from new items
					const otherResults = searchResults.filter((item: OptionType) => !selectedIdsSet.has(item?.value));

					// Deduplicate: use selectedOptions from state, then add new items
					// This ensures selected items stay visible and don't cause jumping
					const allResults = [...selectedOptions];
					const selectedIdsInState = new Set(selectedOptions.map(opt => opt.value));

					// Add other results that aren't already in selectedOptions
					otherResults.forEach(item => {
						if (!selectedIdsInState.has(item.value)) {
							allResults.push(item);
						}
					});

					return allResults;
				}

				return searchResults;
			} catch (error) {
				console.error('Error loading options:', error);

				return selectedOptions;
			} finally {
				setIsLoading(false);
			}
		};

		let debounceTimer: ReturnType<typeof setTimeout> | null = null;
		let lastPromise: Promise<OptionType[]> | null = null;

		const debouncedLoadOptions = (inputValue: string): Promise<OptionType[]> => {
			// For empty input (initial load), call immediately without debounce
			if (inputValue === '') {
				lastPromise = loadOptionsInternal('');

				return lastPromise;
			}

			// Clear existing timer
			if (debounceTimer) {
				clearTimeout(debounceTimer);
			}

			// Create a new promise that will resolve when debounce completes
			return new Promise((resolve) => {
				debounceTimer = setTimeout(async () => {
					lastPromise = loadOptionsInternal(inputValue);
					const result = await lastPromise;
					resolve(result);
				}, 300);
			});
		};

		return debouncedLoadOptions;
	}, [valueFieldName, additionalVariables, loadSelectOptions, selectedOptions, selectedIds]);

	// Handle selection change
	const handleChange = (newValue: MultiValue<OptionType> | SingleValue<OptionType>) => {
		if (isMulti) {
			setSelectedOptions(newValue as OptionType[]);
		} else {
			setSelectedOptions(newValue ? [newValue as OptionType] : []);
		}
	};

	// Get current value for display
	const currentValue = useMemo(() => {
		if (isMulti) {
			return selectedOptions;
		}

		return selectedOptions.length > 0 ? selectedOptions[0] : null;
	}, [selectedOptions, isMulti]);

	// Grid select styles matching AsyncCellSelect from shared-components
	const gridSelectStyles = {
		menuPortal: (base: CSSObjectWithLabel) => ({
			...base,
			zIndex: 9999,
		}),
		menu: (base: CSSObjectWithLabel) => ({
			...base,
			width:        'calc(100% + 4px)',
			margin:       0,
			borderRadius: 0,
		}),
		menuList: (base: CSSObjectWithLabel) => ({
			...base,
			padding: 0,
		}),
		control: (base: CSSObjectWithLabel) => ({
			...base,
			outline:      0,
			border:       0,
			borderRadius: 0,
		}),
		dropdownIndicator: (base: CSSObjectWithLabel) => ({
			...base,
			paddingTop:    0,
			paddingBottom: 0,
		}),
		clearIndicator: (base: CSSObjectWithLabel) => ({
			...base,
			paddingTop:    0,
			paddingBottom: 0,
		}),
		valueContainer: (base: CSSObjectWithLabel) => ({
			...base,
			// @ts-ignore
			whiteSpace: 'nowrap' as Property.WhiteSpace,
			// @ts-ignore
			flexWrap:   'nowrap' as Property.FlexWrap,
		}),
		multiValue: (base: CSSObjectWithLabel) => ({
			...base,
			minWidth:   60,
			lineHeight: 1,
		}),
		option: (base: CSSObjectWithLabel, state: any) => ({
			...base,
			padding:         '8px 12px',
			cursor:          'pointer',
			backgroundColor: state.isSelected
				? '#e3f2fd'
				: state.isFocused
					? '#f5f5f5'
					: 'white',
			color:     'inherit',
			'&:hover': {
				backgroundColor: state.isSelected ? '#e3f2fd' : '#f5f5f5',
			},
		}),
	};

	return (
		<div
			style={{
				width:  '100%',
				height: '100%',
			}}
			onKeyDown={(e) => {
				if (e.key === 'Escape') {
					stopEditing?.(true);
				}
			}}
		>
			<AsyncSelect<OptionType, boolean>
				isMulti={isMulti}
				value={currentValue}
				onChange={handleChange}
				loadOptions={loadOptions}
				defaultOptions={true}
				cacheOptions
				isLoading={isLoading || isPreloading}
				menuIsOpen
				autoFocus
				placeholder="Search..."
				styles={gridSelectStyles}
				menuPortalTarget={document.body}
				components={AsyncSelectComponents}
				// Prevent menu from closing when clicking outside
				closeMenuOnSelect={false}
				// Hide selected options indicator in multi-select
				hideSelectedOptions={false}
			/>
		</div>
	);
});

AsyncGeoCellSelectEditor.displayName = 'AsyncGeoCellSelectEditor';

export default AsyncGeoCellSelectEditor;
